//@ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { Typography, InputAdornment, IconButton, Box } from '@material-ui/core';
import { Modal, Button, Input, SearchScanner } from 'sharedComponents';
import JsBarcode from 'jsbarcode';
import { BarcodeScannerIcon } from 'sharedComponents/Icons';

const jsBarcodeConfigs = {
  format: 'CODE39',
  lineColor: '#000000',
  width: 2,
  height: 50,
  displayValue: true,
};

const SendsReceiptModal = ({
  isOpen,
  onClose,
  classes,
  orderId,
  setModalClosed,
  setLoadingState,
  uploadBarcode,
  barcodeUrl,
  clearSendsReceiptBarcode,
  triggerSendStoreReceipt,
  sentConfirmation,
  error,
}) => {
  const [code, setCode] = React.useState('');
  const [isScannerVisible, setIsScannerVisible] = React.useState(false);
  const [isScannerSuccessful, setIsScannerSuccessful] = React.useState(false);

  /** @type {[*, React.Dispatch<React.SetStateAction<*>>]} */
  const [errorMessage, setErrorMessage] = React.useState('');

  const isConfirmDisabled = React.useMemo(
    () => !!(code.length <= 0 || code.length > 10 || errorMessage || error),
    [code, error, errorMessage],
  );

  /** @type {React.MutableRefObject<*>} */
  const barcodeRef = React.useRef(null);

  const onUploadBarcode = React.useCallback((barcodeParam, code) => {
    uploadBarcode([barcodeParam], code);
  }, []);

  const generateBarcode = () => JsBarcode('#barcodeImg', code, jsBarcodeConfigs);

  const onConfirmHandler = () => {
    setLoadingState(true);

    try {
      generateBarcode();
      onUploadBarcode(barcodeRef?.current?.src, code);
    } catch (error) {
      setErrorMessage(error);
    }
  };

  const codeInputHandler = (value) => {
    if (value === '') {
      setErrorMessage(value);
    }

    setCode(value);
  };

  const clearComponentState = () => {
    setIsScannerVisible(false);
    setLoadingState(false);
    setIsScannerSuccessful(false);
    setCode('');
    setErrorMessage('');
    clearSendsReceiptBarcode();
  };

  const handleScannerEvent = (code) => {
    setIsScannerVisible(false);
    setCode(code);
    setIsScannerSuccessful(true);
  };

  const onScannerClick = () => {
    //If error present, do not open scanner
    if (errorMessage) return;

    setIsScannerVisible(true);
  };

  React.useEffect(() => {
    //If scanner succeeds, submit request to API
    if (isScannerSuccessful) {
      onConfirmHandler();
    }
  }, [isScannerSuccessful]);

  React.useEffect(() => {
    //If a barcodeUrl has been recieved from cloudinary and held in redux, trigger send email
    if (barcodeUrl.length > 0) {
      triggerSendStoreReceipt({ orderId, deliveryReceiptNumber: code, barcodeURL: barcodeUrl });
    }
  }, [barcodeUrl]);

  React.useEffect(() => {
    //If email confirmation received from API, close modal
    if (sentConfirmation) {
      setModalClosed();
    }
  }, [sentConfirmation]);

  React.useEffect(() => {
    if (errorMessage || error) {
      setLoadingState(false);
    }
  }, [errorMessage, error]);

  React.useEffect(() => {
    clearComponentState();
  }, [isOpen]);

  //Function to prevent TypeScript/console error, hides broken image icon when barcode has not been initialized
  const hideImageError = () => "this.style.display='none'";

  return (
    <Modal isOpen={isOpen} onClose={onClose} disablePortal={isScannerVisible}>
      <Typography className={classes.title} variant="h3">
        Generate Delivery Receipt
      </Typography>
      <Typography className={classes.description} variant="h4">
        To create a delivery receipt, scan or manually enter the envelope number.
      </Typography>
      <Box className={classes.barcodeBox} data-testid="sends-receipt-modal-barcode-container">
        <img id="barcodeImg" alt="" onError={hideImageError} ref={barcodeRef} />
      </Box>

      <Input
        type="number"
        id="BarcodeForm"
        name="Barcode Number"
        placeholder="Envelope Number"
        onChange={codeInputHandler}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onScannerClick} data-testid="sends-receipt-modal-scanner-button">
                <BarcodeScannerIcon />
              </IconButton>
            </InputAdornment>
          ),
          inputProps: { inputMode: 'numeric' },
        }}
        error={!!errorMessage || !!error}
        className={classes.inputForm}
      />
      {errorMessage && <div className={classes.defaultError}>{errorMessage}</div>}

      <Button onClick={onConfirmHandler} data-testid="sends-receipt-modal-confirm-button" disabled={isConfirmDisabled}>
        Confirm
      </Button>

      {isScannerVisible && (
        <SearchScanner
          onClose={() => setIsScannerVisible(false)}
          onCodeDetected={(code) => handleScannerEvent(code)}
          positionFixed
          usePortal
        />
      )}
    </Modal>
  );
};

SendsReceiptModal.propTypes = {
  classes: PropTypes.shape({}),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  orderId: PropTypes.string,
  setModalClosed: PropTypes.func,
  setLoadingState: PropTypes.func,
  uploadBarcode: PropTypes.func,
  barcodeUrl: PropTypes.string,
  clearSendsReceiptBarcode: PropTypes.func,
  triggerSendStoreReceipt: PropTypes.func,
  sentConfirmation: PropTypes.string,
  error: PropTypes.string,
};

SendsReceiptModal.defaultProps = {
  classes: {},
  isOpen: false,
  onClose: () => {},
  orderId: '',
  setModalClosed: () => {},
  setLoadingState: () => {},
  uploadBarcode: () => {},
  barcodeUrl: '',
  clearSendsReceiptBarcode: () => {},
  triggerSendStoreReceipt: () => {},
  sentConfirmation: null,
  error: null,
};

export default SendsReceiptModal;
